import React, { useState, useEffect, useCallback } from 'react';
import { X, RotateCcw, Trash2, Clock, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import api from './axiosConfig';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

// 🎯 سلة مهملات لموديل كستم - CustomRecord هو الموديل الوحيد اللي عليه
// SoftDeleteMixin دلوقتي (شوف models.py). الحذف العادي بقى soft delete،
// فالسجلات هنا لسه موجودة فعليًا في الداتابيز - إما استرجاع أو حذف نهائي
// حقيقي (permanent) مفيش رجعة منه
const CustomRecordRecycleBin = ({ isOpen, onClose, slug, onRestored }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isOpen || !slug) return;
    setLoading(true);
    try {
      const res = await api.get(`/custom-models/${slug}/recycle-bin`, { params: { limit: 100 } });
      setItems(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(t('customApps.loadFailed', 'Failed to load this application'));
    } finally {
      setLoading(false);
    }
  }, [isOpen, slug, t]);

  useEffect(() => { load(); }, [load]);

  const handleRestore = async (item) => {
    try {
      await api.post(`/custom-records/${item.id}/restore`);
      toast.success(t('customApps.restored', 'Record restored'));
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setTotal((prev) => prev - 1);
      onRestored?.();
    } catch (err) {
      // 🎯 409 = القيمة البرايمري اتاخدت من سجل فعّال تاني - نعرض الرسالة
      // الحقيقية من الباك إند مش رسالة عامة، عشان اليوزر يعرف يعالجها إزاي
      toast.error(err.response?.data?.detail || t('customApps.restoreFailed', 'Failed to restore'));
    }
  };

  const handlePurge = async (item) => {
    const result = await Swal.fire({
      ...swalTheme(),
      title: t('common.areYouSure', 'Are you sure?'),
      text: t('customApps.purgeConfirm', 'This will permanently delete this record. There is no recycle bin after this - it cannot be recovered!'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalDangerColor(),
      confirmButtonText: t('customApps.yesPurge', 'Yes, delete forever!'),
      cancelButtonText: t('common.cancel', 'Cancel'),
      reverseButtons: isRTL,
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/custom-records/${item.id}/permanent`);
      toast.success(t('common.deleted', 'Deleted'));
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      setTotal((prev) => prev - 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.deleteFailed', 'Delete failed'));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className={`bg-white w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] ${isRTL ? 'text-right' : 'text-left'}`}>
        <div className="bg-[#1a3353] text-white p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <RotateCcw size={20} className="text-orange-400" />
            <div>
              <h3 className="font-black text-sm uppercase tracking-widest">{t('customApps.recycleBin', 'Recycle Bin')}</h3>
              <p className="text-[10px] opacity-60">{t('customApps.recycleBinCount', '{{count}} deleted record(s)', { count: total })}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white/10 p-1 rounded-full"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-slate-50 custom-scrollbar">
          {loading ? (
            <div className="flex justify-center py-20 animate-pulse font-black text-slate-400 uppercase text-[10px]">
              {t('common.loading', 'Loading...')}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-slate-300 italic uppercase text-[10px]">
              {t('customApps.recycleBinEmpty', 'Recycle bin is empty')}
            </div>
          ) : (
            <div className="space-y-2">
              {items.map((item) => (
                <div key={item.id} className={`bg-white border rounded-lg shadow-sm overflow-hidden flex items-center justify-between p-3 ${isRTL ? 'border-r-4 border-r-red-400' : 'border-l-4 border-l-red-400'}`}>
                  <div className="min-w-0">
                    <div className="font-black text-sm text-ink truncate">{item.label}</div>
                    <div className="flex items-center gap-3 text-[10px] font-bold text-slate-500 uppercase mt-1">
                      <span className="flex items-center gap-1"><User size={11} /> {item.deleted_by || '—'}</span>
                      <span className="flex items-center gap-1"><Clock size={11} /> {item.deleted_at ? new Date(item.deleted_at).toLocaleString() : '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleRestore(item)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-primary text-white text-[10px] font-black uppercase hover:bg-primary-hover transition-colors"
                    >
                      <RotateCcw size={12} /> {t('customApps.restore', 'Restore')}
                    </button>
                    <button
                      onClick={() => handlePurge(item)}
                      className="p-2 text-slate-400 hover:text-status-danger hover:bg-status-danger/10 rounded transition-colors"
                      title={t('customApps.deleteForever', 'Delete forever')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomRecordRecycleBin;
